import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import axios from 'axios';

(window as unknown as Record<string, unknown>).Pusher = Pusher;

declare global {
    interface Window {
        /**
         * E2E testleri (Playwright) Reverb sunucusu olmadan çalışır; bu kancaya
         * sahte bir Echo nesnesi koyarak realtime dinleyicilerini gerçek bağlantı
         * olmadan doğrular. Üretimde asla set edilmez.
         */
        __RIZMIR_ECHO_STUB__?: Echo<'reverb' | 'pusher'>;
    }
}

let echoInstance: Echo<'reverb' | 'pusher'> | null = null;

export function getEcho(): Echo<'reverb' | 'pusher'> | null {
  if (echoInstance) {
    return echoInstance;
  }

  // E2E stub'ı: gerçek WebSocket bağlantısı kurulmaz; testler
  // window.__emitEchoEvent ile dinleyicilere event enjekte eder.
  if (typeof window !== 'undefined' && window.__RIZMIR_ECHO_STUB__) {
    echoInstance = window.__RIZMIR_ECHO_STUB__;
    return echoInstance;
  }

  const reverbKey = (import.meta.env.VITE_REVERB_APP_KEY as string | undefined) ?? 'rizmir-key';
  const reverbHost = (import.meta.env.VITE_REVERB_HOST as string | undefined) ?? '127.0.0.1';
  const reverbPort = Number((import.meta.env.VITE_REVERB_PORT as string | undefined) ?? 8080);
  const reverbScheme = (import.meta.env.VITE_REVERB_SCHEME as string | undefined) ?? 'http';

  const pusherKey = import.meta.env.VITE_PUSHER_APP_KEY;
  const pusherCluster = import.meta.env.VITE_PUSHER_APP_CLUSTER;
  const pusherHost = import.meta.env.VITE_PUSHER_HOST;
  const pusherPort = Number(import.meta.env.VITE_PUSHER_PORT || 443);
  const pusherScheme = import.meta.env.VITE_PUSHER_SCHEME || 'https';

  // Broadcasting auth, uygulamanin ana api instance'indan bagimsiz olmali;
  // aksi halde client.ts <-> echo.ts arasinda circular dependency olusur.
  const broadcastingApi = axios.create({
    baseURL: '',
    timeout: 30000,
    headers: { Accept: 'application/json' },
    withCredentials: true,
  });

  broadcastingApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const authorizer = (channel: { name: string }) => ({
    authorize: (
      socketId: string,
      callback: (error: Error | null, data: { auth: string; channel_data?: string } | null) => void,
    ) => {
      broadcastingApi
        .post('/broadcasting/auth', {
          socket_id: socketId,
          channel_name: channel.name,
        })
        .then((response) => callback(null, response.data as { auth: string; channel_data?: string }))
        .catch((error) => callback(error as Error, null));
    },
  });

  // WebSocket heartbeat ayarları: uzun süre sessiz kalan bağlantılar
  // proxy/güvenlik duvarı tarafından kapatılabilir. Düzenli ping/pong
  // göndererek sinyalizasyon gecikmelerini ve ani kopmaları azaltır.
  const heartbeatOptions = {
    activityTimeout: 30000,
    pongTimeout: 15000,
  };

  if (pusherKey) {
    echoInstance = new Echo({
      broadcaster: 'pusher',
      key: pusherKey,
      cluster: pusherCluster || 'eu',
      wsHost: pusherHost,
      wsPort: pusherPort,
      wssPort: pusherPort,
      forceTLS: pusherScheme === 'https',
      enabledTransports: ['ws', 'wss'],
      authorizer,
      ...heartbeatOptions,
    });
  } else if (reverbKey && reverbHost) {
    echoInstance = new Echo({
      broadcaster: 'reverb',
      key: reverbKey,
      wsHost: reverbHost,
      wsPort: reverbPort,
      wssPort: reverbPort,
      forceTLS: reverbScheme === 'https',
      enabledTransports: ['ws', 'wss'],
      authorizer,
      ...heartbeatOptions,
    });
  }

  return echoInstance;
}

export function leaveChannel(channelName: string): void {
  const echo = getEcho();
  if (!echo) return;
  echo.leave(channelName);
}
